/**
 * Customer Map Component
 *
 * Interactive map visualization showing all customers plotted by their delivery locations.
 * Features custom Deeliver-branded markers with tier-based styling (Gold/Silver/Bronze).
 *
 * Features:
 * - Custom markers with customer initials
 * - Tier-based colors (Gold = VIP, Silver = Regular, Bronze = New)
 * - Marker clustering for dense areas
 * - Info popup with customer details and quick actions
 * - Dark/light mode support via OpenStreetMap tiles
 *
 * @module UserManagement/CustomerMap
 */

import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  Typography,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  Button,
  Fade,
  Stack,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  MyLocation as MyLocationIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FilterList as FilterIcon,
  LocalShipping as OrdersIcon,
  AttachMoney as MoneyIcon,
  Schedule as TimeIcon,
  Email as EmailIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup, useMap, ZoomControl } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useQuery } from '@tanstack/react-query';
import authInstance from '../../services/httpClient';
import { queryKeys } from '../../hooks/queries';

// ============================================================================
// TYPES
// ============================================================================

interface CustomerLocation {
  customerId: string;
  name: string;
  initials: string;
  email: string;
  lat: number;
  lng: number;
  suburb: string | null;
  city: string | null;
  province: string | null;
  totalOrders: number;
  totalOrderValue: number;
  lastOrderAt: string | null;
  customerSince: string;
  tier: 'gold' | 'silver' | 'bronze' | 'new';
}

interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

interface LocationsResponse {
  locations: CustomerLocation[];
  total: number;
  bounds: MapBounds | null;
}

interface CustomerMapProps {
  /**
   * No `onClose`. The map used to be a full-viewport overlay that needed its own
   * Back button to escape; it now sits inside the layout, so the sidebar is right
   * there and there is nothing to close.
   */
  onCustomerClick?: (customerId: string) => void;
}

// ============================================================================
// TIER CONFIGURATION - Deeliver Branding
// ============================================================================

const TIER_CONFIG = {
  gold: {
    color: '#FFD700',
    backgroundColor: '#FFF8E1',
    borderColor: '#FFC107',
    shadowColor: 'rgba(255, 193, 7, 0.5)',
    label: 'VIP Customer',
    description: '10+ orders or R5,000+ lifetime',
  },
  silver: {
    color: '#B0BEC5',
    backgroundColor: '#ECEFF1',
    borderColor: '#78909C',
    shadowColor: 'rgba(120, 144, 156, 0.5)',
    label: 'Regular Customer',
    description: '3-9 orders or R1,000+ lifetime',
  },
  bronze: {
    color: '#CD7F32',
    backgroundColor: '#FFF3E0',
    borderColor: '#BF360C',
    shadowColor: 'rgba(191, 54, 12, 0.5)',
    label: 'New Customer',
    description: '1-2 orders',
  },
  new: {
    color: '#06C167',
    backgroundColor: '#E8F5E9',
    borderColor: '#4CAF50',
    shadowColor: 'rgba(76, 175, 80, 0.5)',
    label: 'Signed Up (No Orders)',
    description: 'Has address, no orders yet',
  },
};

// ============================================================================
// CUSTOM MARKER ICONS
// ============================================================================

/**
 * Creates a custom Leaflet divIcon with Deeliver styling.
 * Markers display customer initials with tier-based colors.
 */
const createCustomIcon = (tier: 'gold' | 'silver' | 'bronze' | 'new', initials: string) => {
  const config = TIER_CONFIG[tier];

  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div class="marker-container marker-${tier}" style="
        width: 40px;
        height: 40px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        background: linear-gradient(135deg, ${config.backgroundColor} 0%, ${config.color} 100%);
        border: 3px solid ${config.borderColor};
        box-shadow: 0 4px 12px ${config.shadowColor}, 0 2px 4px rgba(0,0,0,0.2);
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        transition: all 0.3s ease;
        cursor: pointer;
      ">
        <span style="
          transform: rotate(45deg);
          font-weight: 700;
          font-size: 12px;
          color: #333;
          text-shadow: 0 1px 2px rgba(255,255,255,0.5);
          font-family: 'Roboto', sans-serif;
        ">${initials}</span>
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40],
  });
};

/**
 * Creates cluster icon with count and gradient styling.
 */
const createClusterIcon = (cluster: { getChildCount: () => number }) => {
  const count = cluster.getChildCount();
  let size = 'small';
  let sizeClass = 40;

  if (count >= 100) {
    size = 'large';
    sizeClass = 60;
  } else if (count >= 10) {
    size = 'medium';
    sizeClass = 50;
  }

  return L.divIcon({
    html: `
      <div class="cluster-marker cluster-${size}" style="
        width: ${sizeClass}px;
        height: ${sizeClass}px;
        border-radius: 50%;
        background: linear-gradient(135deg, #FF1744 0%, #C4001D 100%);
        border: 3px solid rgba(255,255,255,0.9);
        box-shadow: 0 4px 16px rgba(255, 23, 68, 0.5), 0 2px 8px rgba(0,0,0,0.2);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 700;
        font-size: ${count >= 100 ? '16px' : count >= 10 ? '14px' : '12px'};
        font-family: 'Roboto', sans-serif;
        transition: all 0.3s ease;
        cursor: pointer;
      ">
        ${count}
      </div>
    `,
    className: 'cluster-icon',
    iconSize: L.point(sizeClass, sizeClass),
  });
};

// ============================================================================
// MAP CONTROLS COMPONENT
// ============================================================================

interface MapControlsProps {
  onRefresh: () => void;
  onFitBounds: () => void;
  loading: boolean;
  total: number;
  tierCounts: { gold: number; silver: number; bronze: number; new: number };
  filterTier: string | null;
  onFilterChange: (tier: string | null) => void;
}

const MapControls = ({
  onRefresh,
  onFitBounds,
  loading,
  total,
  tierCounts,
  filterTier,
  onFilterChange,
}: MapControlsProps) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'absolute',
        top: 16,
        left: 16,
        zIndex: 1000,
        p: 2,
        borderRadius: 3,
        minWidth: 280,
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(30, 30, 30, 0.95)'
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <Stack spacing={2}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={600}>
            Customer Map
          </Typography>
          <Box>
            <Tooltip title="Fit to all markers">
              <span>
                <IconButton size="small" onClick={onFitBounds} disabled={loading}>
                  <MyLocationIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Refresh data">
              <span>
                <IconButton size="small" onClick={onRefresh} disabled={loading}>
                  {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary">
          {total.toLocaleString()} customers with delivery locations
        </Typography>

        <Divider />

        <Box>
          <Typography variant="caption" color="text.secondary" gutterBottom display="block">
            Filter by Tier
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label={`All (${total})`}
              size="small"
              onClick={() => onFilterChange(null)}
              variant={filterTier === null ? 'filled' : 'outlined'}
              color={filterTier === null ? 'primary' : 'default'}
            />
            <Chip
              icon={<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: TIER_CONFIG.gold.color, ml: 0.5 }} />}
              label={`Gold (${tierCounts.gold})`}
              size="small"
              onClick={() => onFilterChange('gold')}
              variant={filterTier === 'gold' ? 'filled' : 'outlined'}
              sx={{
                borderColor: filterTier === 'gold' ? TIER_CONFIG.gold.borderColor : undefined,
                bgcolor: filterTier === 'gold' ? TIER_CONFIG.gold.backgroundColor : undefined,
              }}
            />
            <Chip
              icon={<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: TIER_CONFIG.silver.color, ml: 0.5 }} />}
              label={`Silver (${tierCounts.silver})`}
              size="small"
              onClick={() => onFilterChange('silver')}
              variant={filterTier === 'silver' ? 'filled' : 'outlined'}
              sx={{
                borderColor: filterTier === 'silver' ? TIER_CONFIG.silver.borderColor : undefined,
                bgcolor: filterTier === 'silver' ? TIER_CONFIG.silver.backgroundColor : undefined,
              }}
            />
            <Chip
              icon={<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: TIER_CONFIG.bronze.color, ml: 0.5 }} />}
              label={`Bronze (${tierCounts.bronze})`}
              size="small"
              onClick={() => onFilterChange('bronze')}
              variant={filterTier === 'bronze' ? 'filled' : 'outlined'}
              sx={{
                borderColor: filterTier === 'bronze' ? TIER_CONFIG.bronze.borderColor : undefined,
                bgcolor: filterTier === 'bronze' ? TIER_CONFIG.bronze.backgroundColor : undefined,
              }}
            />
          </Stack>
        </Box>

        <Box sx={{ pt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Gold:</strong> 10+ orders or R5,000+ lifetime<br />
            <strong>Silver:</strong> 3-9 orders or R1,000+ lifetime<br />
            <strong>Bronze:</strong> 1-2 orders (new customers)
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
};

// ============================================================================
// FIT BOUNDS COMPONENT
// ============================================================================

interface FitBoundsControllerProps {
  bounds: MapBounds | null;
  triggerFit: number;
}

const FitBoundsController = ({ bounds, triggerFit }: FitBoundsControllerProps) => {
  const map = useMap();

  useEffect(() => {
    if (bounds && triggerFit > 0) {
      const leafletBounds = L.latLngBounds(
        [bounds.south, bounds.west],
        [bounds.north, bounds.east]
      );
      map.fitBounds(leafletBounds, { padding: [50, 50], maxZoom: 14 });
    }
  }, [bounds, triggerFit, map]);

  return null;
};

// ============================================================================
// CUSTOMER POPUP COMPONENT
// ============================================================================

interface CustomerPopupProps {
  customer: CustomerLocation;
  onViewDetails: (customerId: string) => void;
}

const CustomerPopup = ({ customer, onViewDetails }: CustomerPopupProps) => {
  const tierConfig = TIER_CONFIG[customer.tier];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const timeSince = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 30) return `${diffDays} days`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months`;
    return `${Math.floor(diffDays / 365)} years`;
  };

  return (
    <Box sx={{ minWidth: 260, p: 1 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${tierConfig.backgroundColor} 0%, ${tierConfig.color} 100%)`,
            border: `2px solid ${tierConfig.borderColor}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: '16px',
            color: '#333',
          }}
        >
          {customer.initials}
        </Box>
        <Box flex={1}>
          <Typography variant="subtitle1" fontWeight={600} lineHeight={1.2}>
            {customer.name}
          </Typography>
          <Chip
            label={tierConfig.label}
            size="small"
            sx={{
              mt: 0.5,
              height: 20,
              fontSize: '10px',
              bgcolor: tierConfig.backgroundColor,
              color: tierConfig.borderColor,
              fontWeight: 600,
            }}
          />
        </Box>
      </Box>

      {/* Stats Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <OrdersIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {customer.totalOrders}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Orders
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {formatCurrency(customer.totalOrderValue)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Lifetime
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {formatDate(customer.lastOrderAt)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Last Order
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {timeSince(customer.customerSince)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Customer
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Location */}
      <Typography variant="caption" color="text.secondary" display="block" mb={2}>
        {[customer.suburb, customer.city, customer.province].filter(Boolean).join(', ')}
      </Typography>

      {/* Action Button */}
      <Button
        fullWidth
        variant="contained"
        size="small"
        onClick={() => onViewDetails(customer.customerId)}
        sx={{
          background: 'linear-gradient(135deg, #FF1744 0%, #C4001D 100%)',
          textTransform: 'none',
          fontWeight: 600,
        }}
      >
        View Full Profile
      </Button>
    </Box>
  );
};

// ============================================================================
// MAIN CUSTOMER MAP COMPONENT
// ============================================================================

export const CustomerMap = ({ onCustomerClick }: CustomerMapProps) => {
  const theme = useTheme();
  const [filterTier, setFilterTier] = useState<string | null>(null);
  const [fitBoundsTrigger, setFitBoundsTrigger] = useState(0);
  const [mapMode, setMapMode] = useState<'customers' | 'notServiced' | 'comingSoon'>('customers');

  // South Africa default center (Johannesburg)
  const defaultCenter: [number, number] = [-26.2041, 28.0473];
  const defaultZoom = 6;

  // Customers data (existing)
  const { data: locationsData, isLoading: loading, error: queryError, refetch } = useQuery({
    queryKey: queryKeys.customers.locations(),
    queryFn: async () => {
      const response = await authInstance.get('admin/users/customers/locations');
      if (!response.data || typeof response.data !== 'object' || !Array.isArray(response.data.locations)) {
        throw new Error('Empty response from server — please refresh the page');
      }
      return response.data as LocationsResponse;
    },
    staleTime: 2 * 60_000,
    gcTime: 10 * 60_000,
  });

  // Not Serviced checks
  const { data: notServicedData, isLoading: notServicedLoading } = useQuery({
    queryKey: ['coverage-checks', 'notServiced'],
    queryFn: async () => {
      const response = await authInstance.get('admin/users/customers/coverage-checks?status=notServiced');
      return response.data as { locations: any[]; total: number; bounds: any };
    },
    staleTime: 2 * 60_000,
    gcTime: 10 * 60_000,
    enabled: mapMode === 'notServiced',
  });

  // Coming Soon checks
  const { data: comingSoonData, isLoading: comingSoonLoading } = useQuery({
    queryKey: ['coverage-checks', 'comingSoon'],
    queryFn: async () => {
      const response = await authInstance.get('admin/users/customers/coverage-checks?status=comingSoon');
      return response.data as { locations: any[]; total: number; bounds: any };
    },
    staleTime: 2 * 60_000,
    gcTime: 10 * 60_000,
    enabled: mapMode === 'comingSoon',
  });

  const locations = locationsData?.locations ?? [];
  const coverageLocations = mapMode === 'notServiced'
    ? (notServicedData?.locations ?? [])
    : (comingSoonData?.locations ?? []);
  const coverageLoading = mapMode === 'notServiced' ? notServicedLoading : comingSoonLoading;
  const bounds = mapMode === 'customers'
    ? (locationsData?.bounds ?? null)
    : mapMode === 'notServiced'
      ? (notServicedData?.bounds ?? null)
      : (comingSoonData?.bounds ?? null);
  const error = queryError ? (queryError instanceof Error ? queryError.message : 'Failed to load locations') : null;

  // Trigger fit bounds when data loads
  useEffect(() => {
    if (bounds) {
      setFitBoundsTrigger((prev) => prev + 1);
    }
  }, [bounds]);

  // Filter locations by tier
  const filteredLocations = useMemo(() => {
    if (!filterTier) return locations;
    return locations.filter((loc) => loc.tier === filterTier);
  }, [locations, filterTier]);

  // Calculate tier counts
  const tierCounts = useMemo(() => {
    return {
      gold: locations.filter((l) => l.tier === 'gold').length,
      silver: locations.filter((l) => l.tier === 'silver').length,
      bronze: locations.filter((l) => l.tier === 'bronze').length,
      new: locations.filter((l) => l.tier === 'new').length,
    };
  }, [locations]);

  // Handle customer click
  const handleCustomerClick = (customerId: string) => {
    if (onCustomerClick) {
      onCustomerClick(customerId);
    }
  };

  // Handle fit bounds
  const handleFitBounds = () => {
    setFitBoundsTrigger((prev) => prev + 1);
  };

  // Map tile URL based on theme
  const tileUrl = theme.palette.mode === 'dark'
    ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
    : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';

  const tileAttribution = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>';

  /**
   * Leaflet needs a definite height, and the map should fill whatever the layout
   * leaves below the app bar.
   *
   * Measured rather than hardcoded: the app bar is 56px tall but the content area
   * starts at y=48, so any arithmetic on a nominal bar height is wrong, and it
   * would go on being wrong the next time the chrome changes. Reading the root's
   * own offset is self-correcting. Safe against a feedback loop because the root
   * is the first thing in the content area — its top doesn't depend on its height.
   */
  const rootRef = useRef<HTMLDivElement>(null);
  const [availableHeight, setAvailableHeight] = useState('calc(100vh - 56px)');

  useLayoutEffect(() => {
    const measure = () => {
      const top = rootRef.current?.getBoundingClientRect().top;
      if (top == null) return;
      const next = `calc(100vh - ${Math.max(0, Math.round(top))}px)`;
      setAvailableHeight((cur) => (cur === next ? cur : next));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  /*
   * Sits INSIDE the layout, not over it.
   *
   * This was `position: fixed; inset: 0; zIndex: 1300`, which covered the app bar
   * and the sidebar — so the only way off the map was its own Back button, and
   * that button sat on top of the legend panel's "Customer Map" heading. Both
   * problems were the overlay. The outer Box owns the height; the inner one is the
   * positioning context every absolutely-placed control below is measured against.
   */
  return (
    <Box ref={rootRef} sx={{ position: 'relative', width: '100%', height: availableHeight }}>
    <Fade in>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: 'background.default',
        }}
      >
        {/* Map Mode Toggle */}
        <Box sx={{ position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 1001, display: 'flex', gap: 0.5, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 2, p: 0.5 }}>
          <Button
            size="small"
            variant={mapMode === 'customers' ? 'contained' : 'text'}
            onClick={() => setMapMode('customers')}
            sx={{ textTransform: 'none', fontSize: 12, minWidth: 80 }}
          >
            Active
          </Button>
          <Button
            size="small"
            variant={mapMode === 'notServiced' ? 'contained' : 'text'}
            onClick={() => setMapMode('notServiced')}
            color={mapMode === 'notServiced' ? 'error' : 'inherit'}
            sx={{ textTransform: 'none', fontSize: 12, minWidth: 100 }}
          >
            Not Serviced
          </Button>
          <Button
            size="small"
            variant={mapMode === 'comingSoon' ? 'contained' : 'text'}
            onClick={() => setMapMode('comingSoon')}
            color={mapMode === 'comingSoon' ? 'warning' : 'inherit'}
            sx={{ textTransform: 'none', fontSize: 12, minWidth: 100 }}
          >
            Coming Soon
          </Button>
        </Box>

        {/* Loading State */}
        {((mapMode === 'customers' && loading && locations.length === 0) || (mapMode !== 'customers' && coverageLoading && coverageLocations.length === 0)) && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1001,
              textAlign: 'center',
            }}
          >
            <CircularProgress size={48} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Loading customer locations...
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1001,
              maxWidth: 400,
            }}
          >
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => refetch()}>
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          </Box>
        )}

        {/* Map Container */}
        <MapContainer
          center={defaultCenter}
          zoom={defaultZoom}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
        >
          <TileLayer url={tileUrl} attribution={tileAttribution} />
          <ZoomControl position="bottomright" />
          <FitBoundsController bounds={bounds} triggerFit={fitBoundsTrigger} />

          {/* Clustered Markers — Customers Mode */}
          {mapMode === 'customers' && (
            <MarkerClusterGroup
              chunkedLoading
              iconCreateFunction={createClusterIcon}
              maxClusterRadius={60}
              spiderfyOnMaxZoom
              showCoverageOnHover={false}
              zoomToBoundsOnClick
            >
              {filteredLocations.map((location) => (
                <Marker
                  key={location.customerId}
                  position={[location.lat, location.lng]}
                  icon={createCustomIcon(location.tier, location.initials)}
                >
                  <Popup maxWidth={300} minWidth={280}>
                    <CustomerPopup
                      customer={location}
                      onViewDetails={handleCustomerClick}
                    />
                  </Popup>
                </Marker>
              ))}
            </MarkerClusterGroup>
          )}

          {/* Coverage Checks Mode */}
          {mapMode !== 'customers' && (
            <MarkerClusterGroup chunkedLoading maxClusterRadius={50}>
              {coverageLocations.map((loc) => {
                const color = loc.coverageStatus === 'active' ? '#06C167'
                  : loc.coverageStatus === 'comingSoon' ? '#ff9800'
                  : '#e53935';
                const icon = L.divIcon({
                  className: 'coverage-marker',
                  html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.4)"></div>`,
                  iconSize: [14, 14],
                  iconAnchor: [7, 7],
                });
                return (
                  <Marker key={loc.id} position={[loc.lat, loc.lng]} icon={icon}>
                    <Popup maxWidth={250}>
                      <div style={{ fontFamily: 'sans-serif', fontSize: 13 }}>
                        <strong>{loc.name}</strong><br />
                        <span style={{ color }}>{loc.coverageStatus === 'active' ? 'Serviced' : loc.coverageStatus === 'comingSoon' ? 'Coming Soon' : 'Not Serviced'}</span><br />
                        {loc.storeName && <><small>Store: {loc.storeName}</small><br /></>}
                        <small>Checks: {loc.checkCount} | Last: {new Date(loc.lastChecked).toLocaleDateString('en-ZA')}</small>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MarkerClusterGroup>
          )}
        </MapContainer>

        {/* Controls Panel — only in customers mode (tier filters don't apply to coverage) */}
        {mapMode === 'customers' && (
          <MapControls
            onRefresh={() => refetch()}
            onFitBounds={handleFitBounds}
            loading={loading}
            total={locations.length}
            tierCounts={tierCounts}
            filterTier={filterTier}
            onFilterChange={setFilterTier}
          />
        )}

        {/* Coverage stats summary */}
        {mapMode !== 'customers' && (
          <Box sx={{
            position: 'absolute', bottom: 24, right: 24, zIndex: 1001,
            bgcolor: 'background.paper', borderRadius: 2, boxShadow: 3, p: 2, minWidth: 180,
          }}>
            <Typography variant="caption" fontWeight={700} gutterBottom display="block">
              {mapMode === 'notServiced' ? 'Not Serviced' : 'Coming Soon'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {coverageLocations.length} location{coverageLocations.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        )}
      </Box>
    </Fade>
    </Box>
  );
};

export default CustomerMap;
